import * as React from 'react';
import { useIntl } from 'react-intl';
import ActionsMenu from './ActionsMenu';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';
import ConfirmDialog from '../../ConfirmDialog';
import { TEmployeeRecord } from '../interfaces';
import * as EmployeeService from '../EmployeeService';
import ActivateUser from '../ActivateUser';
import BlockUser from '../BlockUser';
import ResetUser from '../ResetBlockUser';
import UnBlockUser from '../UnBlockUser';
import useAnalytics from '../../../hooks/useAnalytics';


export interface IActionsProps<T extends TEmployeeRecord> {
  data: T;
  updateTable: () => void;
}

declare global {
  interface Window {
    resizeTimeout?: number;
  }
}

function Actions<T extends TEmployeeRecord>(props: IActionsProps<T>): JSX.Element {
  const [isActionsMenuOpened, setIsActionsMenuOpened] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const { formatMessage: f } = useIntl();
  const [isRemoveConfirmDialogOpen, setIsRemoveConfirmDialogOpen] = React.useState<boolean>(false);
  const [isActivateConfirmDialogOpen, setIsActivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isDeactivateConfirmDialogOpen, setIsDeactivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isReactivateConfirmDialogOpen, setIsReactivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isResetConfirmDialogOpen, setIsResetConfirmDialogOpen] = React.useState<boolean>(false);
  const [isMenuVisible, setIsMenuVisible] = React.useState(true);
  const menuRef: React.RefObject<HTMLDivElement> = React.createRef();
  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();
  const lastPosition = React.useRef<{ top: number; left: number }>({ top: 0, left: 0 });
  const { trackEvent } = useAnalytics();

  useOutsideClick(
    clickOutsideRef,
    (e) => {
      setIsActionsMenuOpened(false);
    },
    [menuRef]
  );

  useKeyPress(ESCAPE_KEY, () => {
    setIsActionsMenuOpened(false);
  });

  const isElementVisible = React.useCallback((): boolean => {
    const rect = clickOutsideRef.current?.getBoundingClientRect();
    const header = document.getElementById('cmd-table-header');
    const table = document.getElementById('cmd-table');

    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const tableBottom = table ? table.getBoundingClientRect().bottom : 0;

    return !!(
      rect &&
      rect.top >= headerBottom && // Ensure the top of the element is below the bottom of the header
      rect.bottom <= tableBottom && // Ensure the bottom of the element is above the bottom of the table
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth)
    );
  }, [clickOutsideRef]);

  const updateMenuPosition = React.useCallback(() => {
    if (clickOutsideRef.current) {
      const rect = clickOutsideRef.current.getBoundingClientRect();
      const table = document.getElementById('cmd-table');
      const tableBottom = table ? table.getBoundingClientRect().bottom : 0;
      const menuHeight = menuRef?.current?.getBoundingClientRect().height || 176;
      // const menuHeight = 176;

      const newPosition = {
        top: rect.bottom + 9 + window.scrollY,
        left: rect.right - 129 + window.scrollX,
      };

      if (rect.bottom + menuHeight > tableBottom) {
        newPosition.top = rect.top - menuHeight + window.scrollY;
      }

      // Only update if position actually changes
      if (
        newPosition.top !== lastPosition.current.top ||
        newPosition.left !== lastPosition.current.left
      ) {
        setMenuPosition(newPosition);
        lastPosition.current = newPosition; // Update the last known position
      }
    }
  }, [clickOutsideRef, menuRef]);

  const handleResizeOrScroll = React.useCallback(() => {
    clearTimeout(window.resizeTimeout);
    window.resizeTimeout = window.setTimeout(() => {
      if (isActionsMenuOpened) {
        if (isElementVisible()) {
          updateMenuPosition();
          setIsMenuVisible(true);
        } else {
          setIsMenuVisible(false);
          // setIsActionsMenuOpened(false);
        }
      }
    }, 100);
  }, [isActionsMenuOpened, updateMenuPosition, isElementVisible]);

  React.useEffect(() => {
    window.addEventListener('scroll', handleResizeOrScroll);
    window.addEventListener('resize', handleResizeOrScroll);

    // Initial position update
    if (isActionsMenuOpened) {
      updateMenuPosition();
      setIsMenuVisible(isElementVisible());
    }

    return () => {
      window.removeEventListener('scroll', handleResizeOrScroll);
      window.removeEventListener('resize', handleResizeOrScroll);
      clearTimeout(window.resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResizeOrScroll, isActionsMenuOpened, updateMenuPosition]);

  const handleActions = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open table actions menu']);

    const newState = !isActionsMenuOpened;
    setIsActionsMenuOpened(newState);
    if (newState) {
      const isVisible = isElementVisible();
      setIsMenuVisible(isVisible);
      if (isVisible) {
        updateMenuPosition();
      }
    }
  };

  const handleRemoveConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open remove employee dialog']);

    setIsActionsMenuOpened(false);
    setIsRemoveConfirmDialogOpen(true);
  };

  const handleActivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open activate employee dialog']);

    setIsActionsMenuOpened(false);
    setIsActivateConfirmDialogOpen(true);
  };

  const handleReactivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open reactivate employee dialog']);

    setIsActionsMenuOpened(false);
    setIsReactivateConfirmDialogOpen(true);
  };

  const handleDeactivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open deactivate employee dialog']);

    setIsActionsMenuOpened(false);
    setIsDeactivateConfirmDialogOpen(true);
  };

  const handleResetConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Open reset employee dialog']);

    setIsActionsMenuOpened(false);
    setIsResetConfirmDialogOpen(true);
  };

  const handleCancelRemove = () => {
    setIsRemoveConfirmDialogOpen(false);
  };

  const handleSubmitConfirmDialog = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['employee', 'click', 'Confirm remove employee']);
    const res = await EmployeeService.remove(props.data.id, props.data.customer.id);
    if (res) {
      props.updateTable();
      setIsRemoveConfirmDialogOpen(false);
    }
  };

  return (
    <div
      ref={clickOutsideRef}
      style={{
        zIndex: isActionsMenuOpened ? 10 : 4,
        margin: 'auto',
      }}
      key={props.data.id}
    >
      <div className="">
        <a
          href="#"
          className="text-gray-lighten"
          onClick={handleActions}
          style={{ padding: '15px', margin: '-15px' }}
          id={`rideActions-${props.data.id}`}
          data-cy="actions-menu-ride"
        >
          <i className="cmd-icons cmd-icon-more-horizontal fs-4" id={`ride-${props.data.id}`} />
          <span className="sr-only">{f({ id: 'table.actions' })}</span>
        </a>
        {isActionsMenuOpened && isMenuVisible && (
          <ActionsMenu
            data={props.data}
            removeConfirmModalCallback={handleRemoveConfirmDialog}
            activateConfirmModalCallback={handleActivateConfirmDialog}
            deactivateConfirmModalCallback={handleDeactivateConfirmDialog}
            reactivateConfirmModalCallback={handleReactivateConfirmDialog}
            resetConfirmModalCallback={handleResetConfirmDialog}
            position={menuPosition}
            ref={menuRef}
          />
        )}
      </div>
      {isRemoveConfirmDialogOpen && (
        <ConfirmDialog
          title={`${f({ id: 'employee.confirm.dialog.remove.title' })} ${props.data.firstName} ${props.data.lastName}`}
          cancelCallback={handleCancelRemove}
          continueCallback={handleSubmitConfirmDialog}
          cancelButtonTitle={f({
            id: 'form.cancel',
          })}
          continueButtonTitle={f({
            id: 'table.deleteBtn',
          })}
        >
          {f({ id: 'table.confirm.dialog.remove.message' })}
        </ConfirmDialog>
      )}
      {isDeactivateConfirmDialogOpen && (
        <BlockUser
          selectedIds={[props.data.userId]}
          handleConfirmDialogOpen={setIsDeactivateConfirmDialogOpen}
          fullname={`${props.data.firstName} ${props.data.lastName}`}
          updateTable={props.updateTable}
        />
      )}
      {isActivateConfirmDialogOpen && (
        <ActivateUser
          selectedIds={[props.data.userId]}
          handleConfirmDialogOpen={setIsActivateConfirmDialogOpen}
          email={props.data.email}
          updateTable={props.updateTable}
        />
      )}
      {isReactivateConfirmDialogOpen && (
        <UnBlockUser
          selectedIds={[props.data.userId]}
          handleConfirmDialogOpen={setIsReactivateConfirmDialogOpen}
          fullname={`${props.data.firstName} ${props.data.lastName}`}
          updateTable={props.updateTable}
        />
      )}
      {isResetConfirmDialogOpen && (
        <ResetUser
          selectedIds={[props.data.userId]}
          handleConfirmDialogOpen={setIsResetConfirmDialogOpen}
          fullname={`${props.data.firstName} ${props.data.lastName}`}
          updateTable={props.updateTable}
        />
      )}
    </div>
  );
}

export default Actions;
